import { useStore } from '@contexts/store'
import { useGetShopProducts } from '@framework/products/get-shop-products'
import { extractUUID } from '@helper/extractUUID'
import { BannersFromApi } from '@interfaces/banner'
import { ROUTES } from '@utils/routes'
import { isEmpty, uniqBy } from 'lodash'
import { useEffect, useState } from 'react'
import { firstBy } from 'thenby'

type Link = {
  type: string
  value: string
}
const generateLink = (link: Link): string | null => {
  if (link?.type?.length < 2 && (!link?.value || link?.value?.length < 2))
    return null
  switch (link?.type) {
    case 'product':
      return `${ROUTES.PRODUCTS}/${link?.value}`
    // case 'category':
    //   return `/category/${extractUUID(link?.value)}`
    // case 'collection':
    //   return `/collection/${extractUUID(link?.value)}`
    // case 'page':
    //   return `/page/${extractUUID(link?.value)}`
    case 'tags':
      return `${ROUTES.TAGS(link.value)}`
    default:
      return link?.value
  }
}

const getRandomObject = (arr: any) => {
  if (!arr || arr?.length < 1) return null
  const randomIndex = Math.floor(Math.random() * arr.length)
  return arr[randomIndex]
}

const NEEDED_BANNER_COUNT = 5

export const useBannerMedia = () => {
  const { storeData: shop } = useStore()
  const { products, isLoading, isError } = useGetShopProducts(
    shop?._id ?? '',
    { isHero: true, limit: NEEDED_BANNER_COUNT },
    {
      revalidateOnFocus: false,
    },
  )

  const [media, setMedia] = useState<BannersFromApi | undefined>()

  const { products: moreProducts, isLoading: isLoadingExtraProducts } =
    useGetShopProducts(
      shop?._id ?? '',
      { limit: NEEDED_BANNER_COUNT },
      {
        revalidateOnFocus: false,
      },
    )

  useEffect(() => {
    const getBannerMedia = () => {
      if (!shop || !products || !moreProducts) return
      const response: BannersFromApi = {
        banners: [],
        type: 'static', // static, dynamic
        selected: null,
      }
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(tomorrow.getDate() + 1)

      let product = products?.data
        ?.filter((dt: any) => dt.media_type === 'IMAGE' && dt.stock > 0) // [TODO] filter disabled, out of stock and archived products
        .sort(firstBy('isHero', -1))

      const productsLength = product?.length
      if (
        productsLength !== undefined &&
        productsLength < NEEDED_BANNER_COUNT
      ) {
        const moreProductsToPush = moreProducts?.data
          ?.filter((dt: any) => dt.media_type === 'IMAGE' && dt.stock > 0)
          ?.slice(0, NEEDED_BANNER_COUNT - productsLength)

        product = uniqBy([...product, ...moreProductsToPush], '_id')
      }

      const shopBannerList = shop?.config?.storefront?.banners

      const shopBanners = shopBannerList?.filter((banner: any) => {
        const endDate =
          banner?.period?.end?.length > 10
            ? banner?.period?.end
            : new Date(tomorrow).toISOString()
        if (banner?.banner?.isImage && banner?.is_active) {
          return (
            new Date(banner?.period?.start).toISOString() <=
              new Date().toISOString() &&
            new Date(endDate).toISOString() >= new Date().toISOString()
          )
        }
        return false
      })

      const shopBannersWithMedia = shopBanners?.map((banner: any) => {
        const isExternalLink = banner?.link?.type === 'url'

        return {
          uuid: banner?.banner?.uuid,
          media_type: banner?.banner?.isImage ? 'IMAGE' : 'VIDEO',
          message: {
            title: banner?.title,
            subtitle: banner?.subtitle,
          },
          show_gradient: banner?.cta?.show_gradient,
          cta_color: banner?.cta?.colour,
          isHero: true,
          link_type: banner?.link?.type,
          is_external_link: isExternalLink,
          link: isExternalLink
            ? banner?.link?.path?.startsWith('http')
              ? banner?.link?.path
              : `https://${banner?.link?.path}`
            : generateLink({
                type: banner?.link?.type,
                value:
                  banner?.link?.type === 'tags'
                    ? banner?.link?.extraPathInfo?.slug.toLowerCase() ||
                      banner?.link?.path
                    : banner?.link?.type === 'product'
                    ? banner?.link?.extraPathInfo?.slug || banner?.link?.path
                    : banner?.link?.extraPathInfo?.slug.toLowerCase() ||
                      banner?.link?.path,
              }) ?? '/',
        }
      })

      const productBannersWithMedia =
        product?.map((product: any) => ({
          uuid: extractUUID(product?.media),
          media_type: product?.media_type,
          message: {
            title: product?.name,
            subtitle: product?.description,
          },
          link_type: 'product',
          is_external_link: false,
          link:
            generateLink({
              type: 'product',
              value: product?.slug ?? product?._id,
            }) ?? '/',
        })) ?? []

      let productBanners =
        productBannersWithMedia?.filter((product: any) => product?.isHero) ?? []

      if (productBanners?.length < 1) {
        productBanners = productBannersWithMedia
      }

      const shopBannersWithMediaAndProduct = [
        ...shopBannersWithMedia,
        ...productBanners,
      ]

      const HeroProduct = getRandomObject(productBanners)
      const randomProduct = getRandomObject(productBannersWithMedia)
      const storeBanners = getRandomObject(shopBannersWithMedia)

      if (shop.plan === 'free') {
        response.banners = productBannersWithMedia
        response.type = 'static'
        response.selected = !isEmpty(HeroProduct) ? HeroProduct : randomProduct
      } else {
        // use both banners and products for paid stores
        // const randomBanner = Math.floor(Math.random() * shopBannersWithMedia.length)
        response.banners = shopBannersWithMediaAndProduct
        response.type =
          shopBannersWithMediaAndProduct?.length >= 1 ? 'dynamic' : 'static'
        response.selected = !isEmpty(storeBanners)
          ? storeBanners
          : !isEmpty(HeroProduct)
          ? HeroProduct
          : randomProduct
      }
      return response
    }

    setMedia(getBannerMedia())
  }, [moreProducts, products, shop])

  return {
    isLoading: isLoading || isLoadingExtraProducts || isError || isEmpty(media),
    media,
  }
}
